import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { depreciationVouchersApi, type CalcResult } from '../lib/api/depreciation-vouchers';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function DepreciationVoucherModal({ open, onClose, onCreated }: Props) {
  const { user } = useAuth();
  const isKtt = user?.role === 'KTT';

  const [period, setPeriod] = useState(currentPeriod());
  const [calc, setCalc] = useState<CalcResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [voucherId, setVoucherId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPeriod(currentPeriod()); setCalc(null); setSelected(new Set());
    setLoading(false); setSaving(false); setPosting(false);
    setErr(null); setVoucherId(null);
  }, [open]);

  useEffect(() => {
    if (!calc) return;
    setSelected(new Set(calc.lines.map((l) => l.assetId)));
  }, [calc]);

  const totalSelected = useMemo(() => {
    if (!calc) return 0;
    return calc.lines
      .filter((l) => selected.has(l.assetId))
      .reduce((s, l) => s + l.monthlyDepreciation, 0);
  }, [calc, selected]);

  if (!open) return null;

  const runCalc = async () => {
    setLoading(true); setErr(null); setCalc(null); setVoucherId(null);
    try {
      const r = await depreciationVouchersApi.calculate(period);
      setCalc(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  };

  const createDraft = async () => {
    if (!calc) return;
    const lines = calc.lines
      .filter((l) => selected.has(l.assetId))
      .map((l) => ({ assetId: l.assetId, monthlyDepreciation: l.monthlyDepreciation }));
    if (lines.length === 0) { setErr('Chưa chọn TS nào để tạo PKT.'); return; }
    setSaving(true); setErr(null);
    try {
      const v = await depreciationVouchersApi.create(period, lines);
      setVoucherId(v.id);
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  };

  const postVoucher = async () => {
    if (!voucherId) return;
    if (!window.confirm('Post PKT sẽ cộng % KH vào từng TS. Không thể hoàn tác. Tiếp tục?')) return;
    setPosting(true); setErr(null);
    try {
      await depreciationVouchersApi.post(voucherId);
      onCreated();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setPosting(false); }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!calc) return;
    setSelected(selected.size === calc.lines.length ? new Set() : new Set(calc.lines.map((l) => l.assetId)));
  };

  const step: 'pick' | 'preview' | 'created' = voucherId ? 'created' : (calc ? 'preview' : 'pick');

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalBox, width: step === 'pick' ? 520 : 960 }}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15h6M9 11h4" /></svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#172033' }}>Tạo PKT khấu hao TSCĐ</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#667085' }}>
                Chọn kỳ · System tự tính KH tháng cho TS active · Review + Post
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeBtn}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
        </div>

        <div style={body}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {step === 'pick' && (
              <>
                <div style={hintBox}>
                  Auto-tính KH tháng theo phương pháp <b>Đường thẳng</b>: KH tháng = Nguyên giá ÷ (Số năm × 12). Ngày bắt đầu KH phải &lt;= cuối kỳ và TS chưa hết KH.
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#334155', fontWeight: 600, marginBottom: 6 }}>Kỳ (YYYY-MM)</label>
                  <input
                    type="month"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    style={{ width: 200, height: 38, padding: '0 12px', border: '1px solid #e4e7ec', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
                  />
                </div>
              </>
            )}

            {step === 'preview' && calc && (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                  <Stat label="Kỳ" value={calc.period} tone="gray" />
                  <Stat label="TS active" value={String(calc.totalAssets)} tone="blue" />
                  <Stat label="Đã tick" value={String(selected.size)} tone="blue" />
                  <Stat label="Tổng KH tháng" value={formatVND(totalSelected)} tone="green" />
                  {calc.skipped.length > 0 && <Stat label="Bỏ qua" value={String(calc.skipped.length)} tone="amber" />}
                </div>

                {calc.lines.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Chi tiết KH ({calc.lines.length} TS)</div>
                      <button type="button" onClick={toggleAll} style={tplBtn}>
                        {selected.size === calc.lines.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                      </button>
                    </div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 340, overflow: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                          <tr style={{ color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.3, borderBottom: '1px solid #e2e8f0', textAlign: 'left', height: 36 }}>
                            <th style={{ padding: '0 8px', width: 30 }} />
                            <th style={{ padding: '0 8px', width: 80 }}>Mã</th>
                            <th style={{ padding: '0 8px' }}>Tên · Loại · BP</th>
                            <th style={{ padding: '0 8px', textAlign: 'right', width: 130 }}>Nguyên giá</th>
                            <th style={{ padding: '0 8px', textAlign: 'right', width: 120 }}>KH tháng</th>
                            <th style={{ padding: '0 8px', width: 130 }}>TK Nợ / Có</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calc.lines.map((l) => (
                            <tr key={l.assetId} style={{ borderTop: '1px solid #f1f5f9', height: 40 }}>
                              <td style={{ padding: '0 8px' }}>
                                <input type="checkbox" checked={selected.has(l.assetId)} onChange={() => toggle(l.assetId)} />
                              </td>
                              <td style={{ padding: '0 8px', fontFamily: 'monospace', fontWeight: 700, color: '#172033' }}>{l.assetCode}</td>
                              <td style={{ padding: '0 8px' }}>
                                <div style={{ color: '#172033', fontWeight: 500 }}>{l.assetName}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {l.categoryName} · {l.department ?? '—'} · {l.effectiveYears}n
                                </div>
                              </td>
                              <td style={{ padding: '0 8px', textAlign: 'right', color: '#172033' }}>{formatVND(l.cost)}</td>
                              <td style={{ padding: '0 8px', textAlign: 'right', fontWeight: 600, color: '#b91c1c' }}>{formatVND(l.monthlyDepreciation)}</td>
                              <td style={{ padding: '0 8px', fontFamily: 'monospace', fontSize: 11 }}>
                                <span style={pill('#eff6ff', '#1d4ed8')}>N 642x</span>{' '}
                                <span style={pill('#fef2f2', '#b91c1c')}>C {l.depreciationAccount ?? '214'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                    Không có TS nào cần tính KH cho kỳ {calc.period}.
                  </div>
                )}

                {calc.skipped.length > 0 && (
                  <details style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                      {calc.skipped.length} TS bị bỏ qua (click để xem)
                    </summary>
                    <ul style={{ margin: '8px 0 0 20px', padding: 0, fontSize: 12, color: '#78350f' }}>
                      {calc.skipped.map((s, i) => (
                        <li key={i}><b>{s.assetCode}</b> · {s.assetName} — {s.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            )}

            {step === 'created' && (
              <div style={{ padding: 20, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>Đã tạo PKT Draft ✓</div>
                <div style={{ marginTop: 8, fontSize: 13, color: '#166534' }}>
                  PKT-KH-{period} · {selected.size} TS · Tổng {formatVND(totalSelected)}
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                  {isKtt
                    ? 'Bấm "Post PKT" để cộng % KH vào TS + đóng kỳ. Không hoàn tác được.'
                    : 'PKT ở trạng thái Draft. KTT cần approve/post.'}
                </div>
              </div>
            )}

            {err && <div style={errBox}>{err}</div>}
          </div>
        </div>

        <div style={footer}>
          <button type="button" onClick={onClose} style={cancelBtn}>{step === 'created' ? 'Đóng' : 'Huỷ'}</button>
          {step === 'pick' && (
            <button type="button" onClick={runCalc} disabled={loading} style={primaryBtn}>
              {loading ? 'Đang tính…' : 'Tính KH'}
            </button>
          )}
          {step === 'preview' && (
            <button type="button" onClick={createDraft} disabled={saving || selected.size === 0} style={primaryBtn}>
              {saving ? 'Đang tạo…' : `Tạo PKT Draft (${selected.size})`}
            </button>
          )}
          {step === 'created' && isKtt && (
            <button type="button" onClick={postVoucher} disabled={posting} style={{ ...primaryBtn, background: '#16a34a' }}>
              {posting ? 'Đang post…' : 'Post PKT'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'gray' | 'blue' | 'green' | 'amber' }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    gray: { bg: '#f1f5f9', fg: '#334155' },
    blue: { bg: '#eff6ff', fg: '#1e40af' },
    green: { bg: '#f0fdf4', fg: '#166534' },
    amber: { bg: '#fffbeb', fg: '#92400e' },
  };
  const c = colors[tone];
  return (
    <div style={{ padding: '6px 10px', background: c.bg, color: c.fg, borderRadius: 6 }}>
      <span style={{ opacity: 0.7 }}>{label}: </span><b>{value}</b>
    </div>
  );
}

function pill(bg: string, fg: string): React.CSSProperties {
  return { padding: '1px 6px', background: bg, color: fg, borderRadius: 4, fontWeight: 700 };
}

// ==== Styles ====

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
  zIndex: 60, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalBox: React.CSSProperties = {
  maxWidth: '96vw', maxHeight: 'calc(100vh - 48px)',
  display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)', overflow: 'hidden',
};
const header: React.CSSProperties = {
  flex: '0 0 auto', padding: '16px 22px', borderBottom: '1px solid #e4e7ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const headerIcon: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const closeBtn: React.CSSProperties = {
  width: 30, height: 30, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8,
  cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', background: '#f8fafc', minHeight: 0 };
const footer: React.CSSProperties = {
  flex: '0 0 auto', padding: '12px 22px', borderTop: '1px solid #e4e7ec', background: '#fff',
  display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
};
const hintBox: React.CSSProperties = {
  padding: '10px 12px', background: '#eff6ff', color: '#1e40af', borderRadius: 6,
  fontSize: 12, border: '1px solid #bfdbfe', lineHeight: 1.5,
};
const tplBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 10px',
  background: '#fff', color: '#334155', fontSize: 12, fontWeight: 500,
  border: '1px solid #e4e7ec', borderRadius: 8, cursor: 'pointer',
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 16px', background: '#fff', border: '1px solid #e4e7ec', color: '#334155',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  padding: '9px 20px', background: '#1d2939', color: '#fff', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 8, cursor: 'pointer',
};
const errBox: React.CSSProperties = {
  padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
  color: '#b91c1c', fontSize: 13,
};
